import type { CSSProperties } from "react";
import { Star, ShieldCheck, MapPin, Hammer, Phone, Sparkles, Gift, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import FormEmbed from "@/components/FormEmbed";
import { business, offers } from "@/lib/business";

const badges = [
  { icon: ShieldCheck, label: "Free Estimates" },
  { icon: MapPin, label: "Wakefield Based" },
  { icon: Hammer, label: "Landscaping & Masonry" },
  { icon: Star, label: "Locally Reviewed" },
];

const stats = [
  { value: business.stats.yearsExperience, label: "Years Experience" },
  { value: business.stats.projectsCompleted, label: "Projects Completed" },
  { value: business.stats.townsServed, label: "Towns Served" },
];

/**
 * Above-the-fold hero.
 *
 * Every entrance here is a CSS animation rather than a framer-motion one.
 * The headline and tagline are the page's Largest Contentful Paint element,
 * so gating their opacity on a JavaScript animation library delayed the
 * paint until that library had loaded and run. The offsets, durations,
 * delays and easing curves are unchanged, so the motion looks identical.
 */
const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden hero-cinematic hero-curve-bottom"
    >
      <img
        src="/assets/images/hero-landscaping-masonry-wakefield-ma.webp"
        alt="Stone pathway winding through a mature landscaped garden in Wakefield, Massachusetts"
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        width={500}
        height={333}
        loading="eager"
        decoding="async"
        {...{ fetchpriority: "high" }}
      />

      <div
        aria-hidden="true"
        className="absolute top-24 right-16 w-96 h-96 rounded-full z-[1] animate-glow-pulse"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.4), transparent 70%)" }}
      />

      <div className="relative z-10 container-max px-4 py-28 md:py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: copy, left-aligned and vertically centred */}
          <div className="lg:col-span-7 text-left">
            <div className="enter" style={{ "--enter-y": "28px" } as CSSProperties}>
              <div
                className="enter inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary-bright)/0.45)] bg-[hsl(var(--primary-bright)/0.15)] px-4 py-1.5 text-xs sm:text-sm font-bold uppercase tracking-[0.16em] text-[hsl(var(--primary-bright))] backdrop-blur-sm mb-7"
                style={{ "--enter-x": "-18px", "--enter-dur": "500ms", "--enter-delay": "150ms" } as CSSProperties}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Wakefield, MA · Since 2010
              </div>

              <h1 className="font-heading text-white font-bold tracking-tight leading-[1.02] mb-6" style={{ fontSize: "clamp(2.5rem, 5.6vw, 4.75rem)" }}>
                Landscaping &amp;{" "}
                <span className="text-gradient-on-dark">Masonry</span>
                <br />
                Built to Last in
                <br />
                New England
              </h1>

              {/* Tagline — solid white at full weight for guaranteed contrast
                  against the cinematic overlay */}
              <p className="text-white text-base sm:text-lg md:text-xl leading-relaxed mb-9 max-w-2xl font-medium drop-shadow-[0_2px_10px_hsl(100_26%_4%/0.9)]">
                From weekly lawn care and full landscape design to paver patios, retaining walls and
                brick repointing — one local crew handling the whole property, done right the first
                time.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 mb-9">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-8 py-6 rounded-xl btn-shine shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.7)]"
                >
                  Get Free Estimate <ArrowRight className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="text-base gap-2 px-8 py-6 rounded-xl btn-on-dark"
                >
                  <a href={business.phoneHref}>
                    <Phone className="h-5 w-5" /> {business.phone}
                  </a>
                </Button>
              </div>

              <div
                className="enter glass-card rounded-xl px-5 py-3 inline-flex items-start sm:items-center gap-3 max-w-xl"
                style={{ "--enter-y": "10px", "--enter-dur": "500ms", "--enter-delay": "550ms" } as CSSProperties}
              >
                <Gift className="h-5 w-5 text-[hsl(var(--primary-bright))] shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-white text-sm font-medium">
                  <span className="font-bold text-[hsl(var(--primary-bright))]">
                    {offers.returning.short}
                  </span>{" "}
                  for returning customers — plus {offers.referral.short.toLowerCase()}.
                </span>
              </div>
            </div>
          </div>

          {/* Right: estimate form widget, embedded at natural size */}
          <div
            className="enter lg:col-span-5 relative"
            style={{ "--enter-x": "36px", "--enter-dur": "700ms", "--enter-delay": "300ms" } as CSSProperties}
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl pointer-events-none -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.4), transparent 70%)" }}
            />
            {/*
              The min-height is a floor, not a constraint — the card grows
              freely past it and the embed is never clipped.

              It exists because the provider's form collapses its iframe to
              zero height for several seconds while its Cloudflare Turnstile
              challenge initialises, then restores it. With the hero grid
              vertically centred, that collapse and restore dragged the copy
              column ~700px and was the entire 0.149 Cumulative Layout Shift.
              780px sits below the card's natural height at every viewport
              width measured from 360px to 1920px (the smallest was 806px),
              so it can never introduce whitespace.
            */}
            <div className="relative min-h-[780px] rounded-2xl bg-card border border-border/60 shadow-premium-lg p-4 sm:p-5">
              <div className="px-1 pb-3">
                <h2 className="font-heading text-xl sm:text-2xl text-foreground font-bold tracking-tight">
                  Request Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Tell us about your project — we reply within one business day.
                </p>
              </div>
              <FormEmbed instance="hero" />
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div
          className="enter mt-14 md:mt-16 glass-card rounded-2xl p-6 md:p-7 gradient-border"
          style={{ "--enter-y": "20px", "--enter-dur": "500ms", "--enter-delay": "700ms" } as CSSProperties}
        >
          <div className="flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-6">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="font-heading text-3xl md:text-4xl text-white font-bold tracking-tight">
                    {stat.value}
                  </div>
                  <div className="text-white/80 text-[11px] font-semibold uppercase tracking-[0.14em] mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {badges.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg bg-[hsl(var(--primary-bright)/0.2)] flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[hsl(var(--primary-bright))]" />
                  </span>
                  <span className="text-white/90 text-xs font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
